import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Settings, 
  Database, 
  Shield, 
  Activity,
  Trash2,
  Edit2,
  Plus,
  RefreshCw,
  AlertCircle,
  Tag
} from 'lucide-react';
import CategoriesManagement from './CategoriesManagement';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function TechnicalSettings({ currentUser }) {
  return (
    <div className="space-y-6 fade-in">
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Paramètres Techniques</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Administration et configuration avancée du système</p>
        </div>
        <Badge className="bg-red-600 text-white px-4 py-2 text-sm">
          <Shield className="w-4 h-4 mr-2" />
          Super Admin - Accès Technique
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="categories">
            <Database className="w-4 h-4 mr-2" />
            Catégories
          </TabsTrigger>
          <TabsTrigger value="roles">
            <Shield className="w-4 h-4 mr-2" />
            Rôles
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="w-4 h-4 mr-2" />
            Activité
          </TabsTrigger>
          <TabsTrigger value="system">
            <Settings className="w-4 h-4 mr-2" />
            Système
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Gestion des Catégories Globales</CardTitle>
                <CardDescription>Gérez les catégories de revenus et dépenses pour toutes les pharmacies</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mt-4">
                <CategoriesManagement currentUser={currentUser} isGlobalMode={true} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles">
          <Card>
            <CardHeader>
              <CardTitle>Gestion des Rôles et Permissions</CardTitle>
              <CardDescription>Configurez les permissions pour chaque type d'utilisateur</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Card className="border-l-4 border-l-purple-500">
                  <CardHeader>
                    <CardTitle className="text-lg">Super Admin</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p>✅ Gestion technique de la plateforme</p>
                      <p>✅ Activation/suspension des pharmacies</p>
                      <p>✅ Gestion des catégories globales</p>
                      <p>❌ Pas d'accès aux données confidentielles des pharmacies</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader>
                    <CardTitle className="text-lg">Admin Pharmacie</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p>✅ Accès complet aux données de sa pharmacie</p>
                      <p>✅ Gestion des utilisateurs (employés)</p>
                      <p>✅ Toutes les fonctionnalités (Dashboard, Analytics, Clôtures)</p>
                      <p>✅ Configuration de sa pharmacie</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardHeader>
                    <CardTitle className="text-lg">Employé / Caissier</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p>✅ Ajout de revenus</p>
                      <p>✅ Ajout de dépenses</p>
                      <p>✅ Gestion sécurité sociale</p>
                      <p>✅ Gestion fournisseurs</p>
                      <p>❌ Pas d'accès aux analyses</p>
                      <p>❌ Pas d'accès aux clôtures</p>
                      <p>❌ Pas de gestion des utilisateurs</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Journal d'Activité de la Plateforme</CardTitle>
              <CardDescription>Surveillance des connexions et actions importantes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium">Fonctionnalité en développement</p>
                    <p className="text-xs text-gray-600">Le système de logs sera disponible prochainement</p>
                  </div>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Statistiques d'Utilisation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-2xl font-bold text-blue-600">--</p>
                        <p className="text-xs text-gray-600">Catégories actives</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">-</p>
                        <p className="text-xs text-gray-600">Connexions aujourd'hui</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system">
          <Card>
            <CardHeader>
              <CardTitle>Configuration Système</CardTitle>
              <CardDescription>Paramètres avancés de la plateforme</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Actions de Maintenance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => fetchCategories()}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Actualiser les Catégories
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => toast.info('Fonctionnalité en développement')}
                    >
                      <Database className="w-4 h-4 mr-2" />
                      Sauvegarde Base de Données
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Informations Système</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Version :</span>
                        <span className="font-medium">1.0.0</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Environnement :</span>
                        <Badge variant="outline">Production</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base de données :</span>
                        <span className="font-medium">MongoDB</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TechnicalSettings;
